import { useState, useMemo, useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import Header from "../../domain/Header/Header";
import PlaceTable from "../../domain/PlaceTable/PlaceTable";
import Footer from "../../domain/Footer/Footer";
import Filter from "../../domain/Filters/Filters";
import srcSpinner from "../../assets/images/blue-spinner.gif";
import usePlaceDesignData from "../../hooks/usePlaceDesignData";
import Modal from "../../ui/Modal/Modal";
import ErrorModal from "../../ui/Modal/ErrorModal";
import { useToolContext } from "../../domain/ToolContext/ToolContext";

const LoaderBase = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
`;

const TableLoader = styled.img`
  height: 28px;
  width: 28px;
`;

const MainContainer = styled.div`
  background: var(--white);
  height: 100vh;
  max-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const TableContainer = styled.div`
  padding: 0px 20px;
  padding-bottom: 100px;
  overflow-y: auto;
  flex: 1;
`;

const ErrorText = styled.p`
  color: var(--error);
  font-size: 30px;
  font-weight: 300;
  text-align: center;
`;

const Home = (): JSX.Element => {
  const { t } = useTranslation();
  const context = useToolContext();

  const [searchValue, setSearchValue] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, error, loading } = usePlaceDesignData();

  const closeModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (error) {
      setIsModalOpen(true);
    }
  }, [error]);

  const filteredPlaceData = useMemo(() => {
    if (!searchValue) return data;

    return data.filter((data) =>
      data.place.name?.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [searchValue, data]);

  return (
    <>
      {isModalOpen && error && (
        <Modal>
          <ErrorModal
            header={error.message.split("|")[0]}
            message={error.message.split("|")[1]}
            onClose={closeModal}
          />
        </Modal>
      )}
      <MainContainer>
        <Header />
        <Filter onSearchPlaces={setSearchValue} />
        <TableContainer>
          <PlaceTable placeData={filteredPlaceData} />
          {loading && (
            <LoaderBase>
              <TableLoader src={srcSpinner} />
            </LoaderBase>
          )}
          {context.isOffline && (
            <ErrorText data-testid="offline-message">
              {t("table.offline_error_msg")}
            </ErrorText>
          )}
        </TableContainer>
        <Footer />
      </MainContainer>
    </>
  );
};

export default Home;
